import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReplayIcon from '@mui/icons-material/Replay';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import VerifiedIcon from '@mui/icons-material/Verified';

import {
  createAdLaunchBatch,
  fetchAdLaunchBatch,
  fetchFacebookAdStructureDB,
  fetchLaunchPrefill,
  generateAdCopy,
  retryAdLaunchBatch,
  verifyAdLaunchBatch,
} from '@src/api/creativeStudio';

const CTA_TYPES = [
  'LEARN_MORE', 'SIGN_UP', 'SHOP_NOW', 'BOOK_TRAVEL', 'CONTACT_US', 'GET_OFFER',
  'GET_QUOTE', 'SUBSCRIBE', 'CALL_NOW', 'APPLY_NOW', 'DOWNLOAD', 'ORDER_NOW',
];

const POLL_MS = 3000;

const JOB_STATUS_SX = {
  queued: { bgcolor: 'grey.200', color: 'grey.700' },
  running: { bgcolor: '#e3f2fd', color: '#1565c0' },
  succeeded: { bgcolor: '#c8e6c9', color: '#2e7d32' },
  failed: { bgcolor: '#ffcdd2', color: '#c62828' },
};

const errorMessage = (err, fallback) =>
  err?.response?.data?.detail
  || (typeof err?.response?.data === 'object'
    ? Object.values(err.response.data).flat().join(' ')
    : null)
  || err?.message
  || fallback;

const isTerminal = (status) => ['completed', 'partial', 'failed'].includes(status);

// Ads Manager filtered to specific ads. Meta keeps adding creative "AI
// enhancements" that get switched on server-side after the fact, so every ad
// still gets eyeballed there — passing the ids means the batch opens as one
// filtered list instead of having to be hunted down ad by ad.
const adsManagerUrl = (adAccountId, adIds = []) => {
  const base = `https://adsmanager.facebook.com/adsmanager/manage/ads?act=${adAccountId}`;
  const ids = adIds.filter(Boolean);
  return ids.length ? `${base}&selected_ad_ids=${ids.join(',')}` : base;
};

const allAdIds = (batch) =>
  (batch?.jobs || []).flatMap((job) => (job.created_ads || []).map((ad) => ad.ad_id));

/**
 * Launch many approved tasks into the same ad sets in one action.
 *
 * The shared settings (account, ad sets, page, destination) are filled once;
 * only the ad copy is per-task. Submitting returns straight away — the video
 * uploads and Facebook's transcode happen on a worker — so this dialog then
 * switches to a live progress table the operator can close and come back to.
 */
export default function AdLaunchBatchDialog({ open, tasks = [], clientId, flightId, onClose, onLaunched }) {
  const [form, setForm] = useState({
    ad_account_id: '', campaign_id: '', ad_set_ids: [],
    cta_type: 'LEARN_MORE', destination_url: '', display_link: '', page_id: '', page_name: '',
  });
  const [copyByTask, setCopyByTask] = useState({});
  const [structure, setStructure] = useState([]);
  const [landingPages, setLandingPages] = useState([]);
  const [loadingStructure, setLoadingStructure] = useState(false);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [batch, setBatch] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const pollRef = useRef(null);

  const taskIds = useMemo(() => tasks.map((t) => t.id), [tasks]);

  // --- Prefill from the first task, then load the account's structure ---
  useEffect(() => {
    if (!open || !tasks.length) return;
    setError('');
    setBatch(null);
    setCopyByTask(
      Object.fromEntries(tasks.map((t) => [
        String(t.id),
        { headline: '', primary_text: '', description: '', destination_url: '' },
      ])),
    );

    let cancelled = false;
    (async () => {
      setLoadingStructure(true);
      try {
        const [prefillRes, structureRes] = await Promise.all([
          fetchLaunchPrefill(tasks[0].id),
          clientId ? fetchFacebookAdStructureDB(clientId) : Promise.resolve({ data: [] }),
        ]);
        if (cancelled) return;
        const prefill = prefillRes?.data || {};
        setForm((prev) => ({
          ...prev,
          destination_url: prefill.destination_url || prev.destination_url,
          display_link: prefill.display_link || prev.display_link,
          page_id: prefill.page_id || prev.page_id,
          page_name: prefill.page_name || prev.page_name,
        }));
        setStructure(structureRes?.data || []);
        setLandingPages(prefill.landing_pages || []);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, 'Could not load Facebook structure'));
      } finally {
        if (!cancelled) setLoadingStructure(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, clientId, tasks]);

  // --- Poll the batch while it runs ---
  const pollBatch = useCallback(async (batchId) => {
    try {
      const res = await fetchAdLaunchBatch(batchId);
      const next = res?.data;
      setBatch(next);
      if (next && isTerminal(next.status)) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        onLaunched?.(next);
      }
    } catch (_err) {
      // A dropped poll is not worth surfacing — the next tick retries.
    }
  }, [onLaunched]);

  useEffect(() => {
    if (!batch?.id || isTerminal(batch.status)) return undefined;
    pollRef.current = setInterval(() => pollBatch(batch.id), POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [batch?.id, batch?.status, pollBatch]);

  const adAccounts = useMemo(() => (Array.isArray(structure) ? structure : []), [structure]);
  const campaigns = useMemo(() => {
    const account = adAccounts.find((a) => String(a.account_id) === String(form.ad_account_id));
    return account?.campaigns || [];
  }, [adAccounts, form.ad_account_id]);
  const adSets = useMemo(() => {
    const campaign = campaigns.find((c) => String(c.campaign_id) === String(form.campaign_id));
    return campaign?.ad_sets || [];
  }, [campaigns, form.campaign_id]);

  const setCopy = (taskId, field, value) => {
    setCopyByTask((prev) => ({
      ...prev,
      [String(taskId)]: { ...(prev[String(taskId)] || {}), [field]: value },
    }));
  };

  const generateCopyFor = async (task) => {
    setGeneratingFor(task.id);
    try {
      const res = await generateAdCopy(task.id);
      const d = res?.data || {};
      const headline = (d.headlines && d.headlines[0]) || d.headline || '';
      setCopyByTask((prev) => ({
        ...prev,
        [String(task.id)]: {
          ...(prev[String(task.id)] || {}),
          headline: headline || prev[String(task.id)]?.headline || '',
          primary_text: d.primary_text || prev[String(task.id)]?.primary_text || '',
          description: d.description || prev[String(task.id)]?.description || '',
        },
      }));
    } catch (err) {
      setError(errorMessage(err, `Ad copy generation failed for ${task.task_code || task.id}`));
    } finally {
      setGeneratingFor(null);
    }
  };

  const generateAllCopy = async () => {
    // Sequential on purpose: the copy generator is an LLM call, and firing 20
    // at once just buys rate-limit errors.
    for (const task of tasks) {
      // eslint-disable-next-line no-await-in-loop
      await generateCopyFor(task);
    }
  };

  const missingCopy = tasks.filter((t) => {
    const entry = copyByTask[String(t.id)] || {};
    return !(entry.headline || '').trim() || !(entry.primary_text || '').trim();
  });

  const canSubmit = Boolean(
    form.ad_account_id && form.ad_set_ids.length && form.destination_url
    && !missingCopy.length && !submitting,
  );

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await createAdLaunchBatch({
        task_ids: taskIds,
        ad_account_id: form.ad_account_id,
        ad_set_ids: form.ad_set_ids,
        page_id: form.page_id,
        page_name: form.page_name,
        cta_type: form.cta_type,
        destination_url: form.destination_url,
        display_link: form.display_link,
        flight: flightId || null,
        copy_by_task: copyByTask,
      });
      setBatch(res?.data || null);
    } catch (err) {
      setError(errorMessage(err, 'Could not queue the launch'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async () => {
    if (!batch?.id) return;
    setVerifying(true);
    try {
      const res = await verifyAdLaunchBatch(batch.id);
      setBatch(res?.data?.batch || batch);
    } catch (err) {
      setError(errorMessage(err, 'Could not re-check the ads'));
    } finally {
      setVerifying(false);
    }
  };

  const handleRetry = async () => {
    if (!batch?.id) return;
    try {
      const res = await retryAdLaunchBatch(batch.id);
      setBatch(res?.data?.batch || null);
    } catch (err) {
      setError(errorMessage(err, 'Retry failed'));
    }
  };

  const counts = batch?.counts || {};
  const done = (counts.succeeded || 0) + (counts.failed || 0);
  const progressPct = counts.total ? Math.round((done / counts.total) * 100) : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {batch
          ? `Launching ${counts.total || tasks.length} ads`
          : `Launch ${tasks.length} ad${tasks.length === 1 ? '' : 's'} to Facebook`}
      </DialogTitle>

      <DialogContent dividers>
        {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}

        {batch ? (
          <Stack spacing={2}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <Typography variant="body2">
                  {done} of {counts.total} finished
                  {counts.ads ? ` · ${counts.ads} ad${counts.ads === 1 ? '' : 's'} created` : ''}
                </Typography>
                {isTerminal(batch.status) ? null : <CircularProgress size={14} />}
              </Stack>
              <LinearProgress variant="determinate" value={progressPct} />
            </Box>

            {isTerminal(batch.status) ? (
              <Alert severity={batch.status === 'completed' ? 'success' : (batch.status === 'failed' ? 'error' : 'warning')}>
                {batch.status === 'completed'
                  ? 'All ads created and PAUSED. Review them in Ads Manager before unpausing — Meta can switch its own creative enhancements back on.'
                  : `${counts.succeeded || 0} succeeded, ${counts.failed || 0} failed. Retrying re-uses videos Facebook already has.`}
              </Alert>
            ) : (
              <Alert severity="info">
                Running on the server — you can close this and come back. Nothing is lost if you navigate away.
              </Alert>
            )}

            {(() => {
              // Meta re-enables its own creative enhancements and ships new
              // ones unannounced, so the read-back is what makes the manual
              // Ads Manager pass an exception review instead of an all-ads one.
              const flagged = (batch.jobs || []).filter((j) => j.has_findings);
              const unknown = [...new Set(
                (batch.jobs || []).flatMap((j) =>
                  ((j.verification || {}).unknown_enhancements || []).map((u) => u.field)),
              )];
              const anyVerified = (batch.jobs || []).some((j) => j.verified_at);
              if (!anyVerified) return null;
              if (!flagged.length) {
                return (
                  <Alert severity="success" icon={<VerifiedIcon fontSize="inherit" />}>
                    Re-read from Facebook: every ad matches what we sent — enhancements opted
                    out, destination and paused status intact.
                  </Alert>
                );
              }
              return (
                <Alert severity="warning" icon={<ReportProblemIcon fontSize="inherit" />}>
                  <strong>{flagged.length} ad{flagged.length === 1 ? '' : 's'} differ from what we sent.</strong>
                  {unknown.length ? (
                    <div>
                      Facebook returned enhancement settings we don’t recognise
                      (<code>{unknown.join(', ')}</code>) — likely a new Meta feature our
                      opt-out list doesn’t cover yet.
                    </div>
                  ) : null}
                </Alert>
              );
            })()}

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Task</TableCell>
                  <TableCell>Headline</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ads</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(batch.jobs || []).map((job) => (
                  <TableRow key={job.id}>
                    <TableCell>
                      <Typography variant="body2">{job.task_code || job.task}</Typography>
                      <Typography variant="caption" color="text.secondary">{job.ad_name}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="caption">{job.headline}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip
                          size="small"
                          label={job.status === 'running' ? job.step_label : job.status}
                          sx={{ height: 20, fontSize: '0.65rem', ...(JOB_STATUS_SX[job.status] || {}) }}
                        />
                        {job.status === 'succeeded' ? <CheckCircleIcon sx={{ fontSize: 15, color: '#2e7d32' }} /> : null}
                        {job.status === 'failed' ? (
                          <Tooltip title={job.error || 'Failed'}>
                            <ErrorOutlineIcon sx={{ fontSize: 15, color: '#c62828' }} />
                          </Tooltip>
                        ) : null}
                      </Stack>
                      {job.has_findings ? (
                        <Tooltip title={((job.verification || {}).drift || [])
                          .map((d) => `${d.field}: expected ${d.expected}, got ${d.actual}`)
                          .join(' · ') || 'Differs from what we sent'}>
                          <Chip
                            size="small"
                            icon={<ReportProblemIcon sx={{ fontSize: 12 }} />}
                            label="check in FB"
                            sx={{ height: 18, fontSize: '0.6rem', mt: 0.25, bgcolor: '#ffe0b2', color: '#e65100' }}
                          />
                        </Tooltip>
                      ) : null}
                      {job.status === 'failed' && job.error ? (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.25 }}>
                          {job.error.slice(0, 140)}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {job.ad_count ? (
                        <Button
                          size="small"
                          endIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                          component="a"
                          target="_blank"
                          rel="noopener noreferrer"
                          href={adsManagerUrl(
                            batch.ad_account_id,
                            (job.created_ads || []).map((ad) => ad.ad_id),
                          )}
                          sx={{ minWidth: 0, px: 0.5 }}
                        >
                          {job.ad_count}
                        </Button>
                      ) : (job.ad_count || 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {loadingStructure ? <LinearProgress /> : null}

            {/* ---- Shared settings: filled once for the whole batch ---- */}
            <Typography variant="subtitle2">Where these ads go</Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                select label="Ad Account" sx={{ minWidth: 260 }} value={form.ad_account_id}
                onChange={(e) => setForm((p) => ({ ...p, ad_account_id: e.target.value, campaign_id: '', ad_set_ids: [] }))}
              >
                {adAccounts.map((a) => (
                  <MenuItem key={a.account_id} value={a.account_id}>{a.account_name} ({a.account_id})</MenuItem>
                ))}
              </TextField>
              <TextField
                select label="Campaign" sx={{ minWidth: 260 }} value={form.campaign_id}
                disabled={!form.ad_account_id}
                onChange={(e) => setForm((p) => ({ ...p, campaign_id: e.target.value, ad_set_ids: [] }))}
              >
                {campaigns.map((c) => (
                  <MenuItem key={c.campaign_id} value={c.campaign_id}>{c.campaign_name} ({c.status})</MenuItem>
                ))}
              </TextField>
            </Stack>

            {form.campaign_id ? (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, maxHeight: 180, overflow: 'auto' }}>
                <Typography variant="caption" color="text.secondary">
                  Every ad in this batch is created in each ad set you pick.
                </Typography>
                <Stack>
                  {adSets.map((as) => (
                    <FormControlLabel
                      key={as.ad_set_id}
                      control={(
                        <Checkbox
                          size="small"
                          checked={form.ad_set_ids.includes(as.ad_set_id)}
                          onChange={(e) => setForm((p) => ({
                            ...p,
                            ad_set_ids: e.target.checked
                              ? [...p.ad_set_ids, as.ad_set_id]
                              : p.ad_set_ids.filter((id) => id !== as.ad_set_id),
                          }))}
                        />
                      )}
                      label={<Typography variant="body2">{as.ad_set_name}</Typography>}
                      sx={{ m: 0 }}
                    />
                  ))}
                </Stack>
              </Box>
            ) : null}

            <Stack direction="row" spacing={2}>
              <TextField
                select label="CTA" sx={{ minWidth: 180 }} value={form.cta_type}
                onChange={(e) => setForm((p) => ({ ...p, cta_type: e.target.value }))}
              >
                {CTA_TYPES.map((cta) => <MenuItem key={cta} value={cta}>{cta.replace(/_/g, ' ')}</MenuItem>)}
              </TextField>
              <Autocomplete
                freeSolo
                fullWidth
                options={landingPages}
                value={form.destination_url}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.url)}
                onInputChange={(_e, value) => setForm((p) => ({ ...p, destination_url: value || '' }))}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Stack sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" noWrap>{option.name || option.url}</Typography>
                        {/* Which system serves this page. The legacy hand-entered
                            URLs are being retired, so labelling the source is how
                            you tell at a glance what's left to clean up. */}
                        {option.source === 'leadshookpro' ? (
                          <Chip label="LeadsHookPro" size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#d1c4e9', color: '#4527a0' }} />
                        ) : (
                          <Chip label="legacy" size="small" variant="outlined" sx={{ height: 16, fontSize: '0.6rem' }} />
                        )}
                        {option.approved ? (
                          <Chip label="approved" size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#c8e6c9', color: '#2e7d32' }} />
                        ) : null}
                        {option.qa_passed === false ? (
                          <Chip label="QA failed" size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#ffcdd2', color: '#c62828' }} />
                        ) : null}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" noWrap>{option.url}</Typography>
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Destination URL"
                    helperText={(() => {
                      const picked = landingPages.find((lp) => lp.url === form.destination_url);
                      const origin = picked
                        ? (picked.source === 'leadshookpro' ? 'from LeadsHookPro' : 'a legacy hand-entered URL')
                        : 'not in the landing-page list';
                      if (landingPages.length > 1) {
                        return `One URL for all ${tasks.length} ads — ${origin}. ${landingPages.length} on file for this client + vertical, so check it's the right one.`;
                      }
                      return `Shared by every ad in this batch — ${origin}.`;
                    })()}
                  />
                )}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Display Link" sx={{ minWidth: 250 }} value={form.display_link}
                onChange={(e) => setForm((p) => ({ ...p, display_link: e.target.value }))}
              />
              <TextField
                label="Facebook Page ID" fullWidth value={form.page_id}
                onChange={(e) => setForm((p) => ({ ...p, page_id: e.target.value }))}
                helperText={form.page_name ? `Auto-filled: ${form.page_name}` : 'Blank uses the Lead Gurus default page'}
              />
            </Stack>

            <Divider />

            {/* ---- Per-task copy ---- */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2">Ad copy ({tasks.length})</Typography>
              <Button
                size="small" variant="outlined" startIcon={<AutoFixHighIcon />}
                disabled={generatingFor !== null}
                onClick={generateAllCopy}
              >
                {generatingFor !== null ? 'Generating…' : 'Generate all with AI'}
              </Button>
              {missingCopy.length ? (
                <Chip size="small" color="warning" label={`${missingCopy.length} still need copy`} sx={{ height: 20, fontSize: '0.65rem' }} />
              ) : null}
            </Stack>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 170 }}>Task</TableCell>
                  <TableCell>Headline</TableCell>
                  <TableCell>Primary text</TableCell>
                  <TableCell sx={{ width: 60 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((task) => {
                  const entry = copyByTask[String(task.id)] || {};
                  return (
                    <TableRow key={task.id}>
                      <TableCell>
                        <Typography variant="caption">{task.task_code || `#${task.id}`}</Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" fullWidth variant="standard" placeholder="Headline"
                          value={entry.headline || ''}
                          onChange={(e) => setCopy(task.id, 'headline', e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" fullWidth variant="standard" multiline maxRows={3}
                          placeholder="Primary text"
                          value={entry.primary_text || ''}
                          onChange={(e) => setCopy(task.id, 'primary_text', e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Generate copy for this ad">
                          <span>
                            <Button
                              size="small" disabled={generatingFor !== null}
                              onClick={() => generateCopyFor(task)}
                            >
                              {generatingFor === task.id ? <CircularProgress size={14} /> : <AutoFixHighIcon fontSize="small" />}
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{batch && !isTerminal(batch.status) ? 'Close (keeps running)' : 'Close'}</Button>
        {batch && allAdIds(batch).length ? (
          <Button
            variant="outlined"
            color="info"
            startIcon={<OpenInNewIcon />}
            component="a"
            target="_blank"
            rel="noopener noreferrer"
            href={adsManagerUrl(batch.ad_account_id, allAdIds(batch))}
          >
            Review {allAdIds(batch).length} ad{allAdIds(batch).length === 1 ? '' : 's'} in Ads Manager
          </Button>
        ) : null}
        {batch && isTerminal(batch.status) ? (
          <Button disabled={verifying} onClick={handleVerify}>
            {verifying ? 'Re-checking…' : 'Re-check against Facebook'}
          </Button>
        ) : null}
        {batch && (counts.failed || 0) > 0 && isTerminal(batch.status) ? (
          <Button variant="outlined" startIcon={<ReplayIcon />} onClick={handleRetry}>
            Retry {counts.failed} failed
          </Button>
        ) : null}
        {batch ? null : (
          <Button variant="contained" disabled={!canSubmit} onClick={handleSubmit}>
            {submitting ? 'Queueing…' : `Launch ${tasks.length} to ${form.ad_set_ids.length} ad set${form.ad_set_ids.length === 1 ? '' : 's'}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
